import { useState, useRef, useEffect } from "react";
import { Select, Tooltip, Dropdown, Spin, notification } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";
import {
  SendOutlined,
  TagOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

import { useUser } from "@/contexts/UserContext";
import { useEntityNotes } from "@/hooks/useEntityNotes";
import type { ApiNote } from "@/services/notesService";
import MentionTextArea from "@/views/shared/MentionTextArea";
import {
  avatarColor,
  collectMentions,
  initials,
  renderNoteText,
} from "@/views/shared/noteMentions";

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function displayName(first?: string, last?: string, email?: string): string {
  return `${first ?? ""} ${last ?? ""}`.trim() || (email ?? "");
}

type Props = {
  entityKey: string;
  trialOptions?: { value: string; label: string }[];
};

export default function NotesTab({ entityKey, trialOptions = [] }: Props) {
  const { userInfo } = useUser();
  const {
    notes,
    mentionableUsers,
    mentionableUsersLoading,
    mentionableUsersFailed,
    isLoading,
    error,
    createNote,
    updateNote,
    deleteNote,
  } = useEntityNotes(entityKey);

  const [draft, setDraft] = useState("");
  const [trialTag, setTrialTag] = useState<string | undefined>(undefined);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const editRef = useRef<TextAreaRef>(null);

  const currentUserName = displayName(
    userInfo?.first_name,
    userInfo?.last_name,
    userInfo?.email,
  );

  useEffect(() => {
    if (editingId !== null && editRef.current?.resizableTextArea?.textArea) {
      const ta = editRef.current.resizableTextArea.textArea;
      ta.focus();
      const len = ta.value.length;
      ta.setSelectionRange(len, len);
    }
  }, [editingId]);

  const reportFailure = (action: string) => (err: unknown) => {
    notification.error({
      message: `Failed to ${action} note`,
      description: err instanceof Error ? err.message : "Unknown error",
      duration: 5,
    });
  };

  const handleSubmit = () => {
    const text = draft.trim();
    if (!text || createNote.isPending) return;
    createNote.mutate(
      {
        text,
        trial_tag: trialTag ?? null,
        mentions: collectMentions(text, mentionableUsers),
      },
      {
        onSuccess: () => {
          setDraft("");
          setTrialTag(undefined);
        },
        onError: reportFailure("save"),
      },
    );
  };

  const handleDelete = (id: number) => {
    deleteNote.mutate(id, { onError: reportFailure("delete") });
  };

  const handleEditStart = (note: ApiNote) => {
    setEditingId(note.id);
    setEditDraft(note.text);
  };

  const handleEditSave = (id: number) => {
    const text = editDraft.trim();
    if (!text || updateNote.isPending) return;
    updateNote.mutate(
      { noteId: id, text, mentions: collectMentions(text, mentionableUsers) },
      {
        onSuccess: () => {
          setEditingId(null);
          setEditDraft("");
        },
        onError: reportFailure("update"),
      },
    );
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditDraft("");
  };

  return (
    <div className="flex flex-col">
      {/* Notes list */}
      <div className="px-5 py-5">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spin />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="text-[14px] font-medium text-[#B83280]">
              Notes could not be loaded
            </div>
            <div className="text-[13px] text-[#9CA3AF] mt-1">
              Refresh the page to try again.
            </div>
          </div>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3">
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H15L21 15V5C21 3.9 20.1 3 19 3ZM13 18V14H17L13 18ZM17 12H12C11.45 12 11 12.45 11 13V19H5V5H19V12H17Z"
                  fill="#C4C9CE"
                />
              </svg>
            </div>
            <div className="text-[14px] font-medium text-[#4B5563]">
              No notes yet
            </div>
            <div className="text-[13px] text-[#9CA3AF] mt-1">
              Add the first note below.
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            {notes.map((note, idx) => {
              const isOwn =
                !!currentUserName && note.author_name === currentUserName;
              const isEditing = editingId === note.id;
              return (
                <div key={note.id}>
                  {idx > 0 && (
                    <div className="border-t border-[#F0F2F4] my-4" />
                  )}
                  <div className="flex gap-3 group">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-bold text-white shrink-0 mt-0.5"
                      style={{ background: avatarColor(note.author_name) }}
                    >
                      {initials(note.author_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Header row */}
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-[14px] font-semibold text-[#1F2933]">
                          {note.author_name}
                        </span>
                        <span className="text-[13px] text-[#9CA3AF]">
                          {note.author_role}
                        </span>
                        {note.trial_tag && (
                          <Tooltip title="Trial context">
                            <span className="flex items-center gap-1 text-[12px] font-medium text-[#0C98D0] bg-[#EBF7FC] rounded-full px-2 py-0.5">
                              <TagOutlined style={{ fontSize: 11 }} />
                              {note.trial_tag}
                            </span>
                          </Tooltip>
                        )}
                        <span className="text-[12px] text-[#C4C9CE] ml-auto shrink-0 flex items-center gap-1.5">
                          {note.edited_at && (
                            <span className="italic">edited</span>
                          )}
                          {formatTimestamp(note.edited_at ?? note.created_at)}
                        </span>
                        {/* Actions — only for own notes, visible on hover */}
                        {isOwn && !isEditing && (
                          <Dropdown
                            trigger={["click"]}
                            menu={{
                              items: [
                                {
                                  key: "edit",
                                  icon: <EditOutlined />,
                                  label: "Edit",
                                  onClick: () => handleEditStart(note),
                                },
                                {
                                  key: "delete",
                                  icon: <DeleteOutlined />,
                                  label: "Delete",
                                  danger: true,
                                  onClick: () => handleDelete(note.id),
                                },
                              ],
                            }}
                          >
                            <button
                              type="button"
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-[#F0F2F4] text-[#9CA3AF] hover:text-[#374151]"
                            >
                              <MoreOutlined style={{ fontSize: 16 }} />
                            </button>
                          </Dropdown>
                        )}
                      </div>

                      {/* Body — either read or edit mode */}
                      {isEditing ? (
                        <div className="flex flex-col gap-2 mt-1">
                          <MentionTextArea
                            textAreaRef={editRef}
                            value={editDraft}
                            onChange={setEditDraft}
                            users={mentionableUsers}
                            usersLoading={mentionableUsersLoading}
                            usersFailed={mentionableUsersFailed}
                            autoSize={{ minRows: 2, maxRows: 8 }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                                handleEditSave(note.id);
                              if (e.key === "Escape") handleEditCancel();
                            }}
                            style={{ fontSize: 14, borderRadius: 8 }}
                          />
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={
                                !editDraft.trim() || updateNote.isPending
                              }
                              onClick={() => handleEditSave(note.id)}
                              className="text-[13px] font-medium text-white bg-[#2B4752] disabled:bg-[#C4C9CE] hover:bg-[#1d3640] rounded-md px-3 py-1 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={handleEditCancel}
                              className="text-[13px] font-medium text-[#6B7280] hover:text-[#374151] transition-colors"
                            >
                              Cancel
                            </button>
                            <span className="text-[12px] text-[#C4C9CE] ml-auto">
                              ⌘ + Enter to save · Esc to cancel
                            </span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-[14px] text-[#374151] leading-relaxed m-0">
                          {renderNoteText(note.text, note.mentions ?? [])}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Compose area */}
      <div className="sticky bottom-0 border-t border-[#F0F2F4] bg-[#FAFBFC] px-5 py-4">
        {trialOptions.length > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <Select
              placeholder={
                <span className="flex items-center gap-1 text-[13px]">
                  <TagOutlined />
                  Tag a trial
                </span>
              }
              value={trialTag}
              allowClear
              options={trialOptions}
              style={{ width: 190 }}
              onChange={(val) => setTrialTag(val)}
            />
          </div>
        )}
        <div className="flex gap-3 items-start">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-bold text-white shrink-0 mt-1"
            style={{ background: avatarColor(currentUserName) }}
          >
            {initials(currentUserName)}
          </div>
          <div className="flex-1 relative">
            <MentionTextArea
              value={draft}
              onChange={setDraft}
              users={mentionableUsers}
              usersLoading={mentionableUsersLoading}
              usersFailed={mentionableUsersFailed}
              placeholder={`Add a note…${trialTag ? ` on ${trialTag}` : ""}`}
              autoSize={{ minRows: 3, maxRows: 6 }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                  handleSubmit();
              }}
              style={{ fontSize: 14, paddingRight: 40, borderRadius: 8 }}
            />
            <Tooltip title="Submit (⌘ + Enter)">
              <button
                type="button"
                disabled={!draft.trim() || createNote.isPending}
                onClick={handleSubmit}
                className="absolute bottom-2.5 right-2.5 text-[#2B4752] disabled:text-[#C4C9CE] hover:text-[#0C98D0] transition-colors"
              >
                <SendOutlined style={{ fontSize: 15 }} />
              </button>
            </Tooltip>
          </div>
        </div>
        <div className="text-right mt-1.5">
          <span className="text-[12px] text-[#C4C9CE]">
            Type @ to tag someone · ⌘ + Enter to submit
          </span>
        </div>
      </div>
    </div>
  );
}
